/**
 * Collection filtering.
 *
 * Upgrades the filter form from a page load to an in-place update. The form
 * is a real GET form against the collection URL, so without this module the
 * filters still work; this only removes the reload.
 *
 * History is updated with pushState so a filtered view stays shareable and
 * the back button behaves.
 */
import { prefersReducedMotion } from 'theme:motion';

const form = document.querySelector('[data-facets]');
const target = document.querySelector('[data-facets-target]');

if (form && target) {
  const panel = form.querySelector('[data-facets-panel]');
  const toggle = form.querySelector('[data-facets-toggle]');
  const resultsLabel = form.querySelector('[data-facets-results]');

  let controller = null;
  let debounceTimer = null;

  toggle?.addEventListener('click', () => {
    const open = panel.hidden;
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
  });

  async function apply({ push = true } = {}) {
    const params = new URLSearchParams(new FormData(form));

    // Empty price bounds would otherwise narrow the results to nothing.
    for (const [key, value] of [...params.entries()]) {
      if (value === '') params.delete(key);
    }

    const url = `${window.location.pathname}?${params}`;

    controller?.abort();
    controller = new AbortController();

    form.setAttribute('data-loading', '');
    target.setAttribute('data-loading', '');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Filter request failed');

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = parsed.querySelector('[data-facets-target]');
      const nextForm = parsed.querySelector('[data-facets]');

      if (nextGrid) target.replaceChildren(...nextGrid.childNodes);

      // Counts and disabled states change with every selection, so the facet
      // list is refreshed from the response while the panel stays open.
      if (nextForm) {
        const nextGroups = nextForm.querySelector('.facets__groups');
        const currentGroups = form.querySelector('.facets__groups');
        if (nextGroups && currentGroups) currentGroups.innerHTML = nextGroups.innerHTML;

        const nextLabel = nextForm.querySelector('[data-facets-results]');
        if (nextLabel && resultsLabel) resultsLabel.textContent = nextLabel.textContent;
      }

      if (push) window.history.pushState({ facets: true }, '', url);

      if (!prefersReducedMotion()) {
        target.animate(
          [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
          { duration: 260, easing: 'cubic-bezier(.16,1,.3,1)' }
        );
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fall back to a normal navigation rather than leaving a stale grid.
      window.location.assign(url);
    } finally {
      form.removeAttribute('data-loading');
      target.removeAttribute('data-loading');
    }
  }

  form.addEventListener('change', (event) => {
    clearTimeout(debounceTimer);
    // Number inputs fire on every keystroke; checkboxes should feel instant.
    const delay = event.target.type === 'number' ? 500 : 0;
    debounceTimer = setTimeout(() => apply(), delay);
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    apply();
  });

  window.addEventListener('popstate', () => window.location.reload());
}
